import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import {
    handleGetSyncData,
    resetState,
    setOrderByDirection,
    setOrderByProperty,
    resetScroll,
    getModalSwitch,
    getLoaderBtn
} from '@app/store/actions';
import {
    getMenuItemById,
    provisionStateSelectData,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    hasOrgPermission,
    Features
} from '@app/helpers';
import {
    ITableHead,
    IListPageBarEnabled,
    IAgentDetail,
    IFabMenu,
    IActiveFilter,
    FilterEnum,
    IDotMenu,
    OrderType,
    Order,
    IFabAction
} from '@app/models';
import { ManageAccount, ManageSystem } from '@app/store/constants';
import {
    FabMenu,
    PageLoader,
    mainMenuTypes,
    IMenuItem,
    PasslyTable,
    IModalData,
    ModalComponent
} from '@app/components';
import { basicService, featureService, syncService } from '@app/services';
import { confirm } from '@app/components/Modal/confirmModal';
import AddAUSIAgentDialog from '@app/pages/directoryManager/dialogs/addAUSISyncAgent.dialog';
import { AxiosError, AxiosResponse } from 'axios';
import EditAUSIAgentDialog from '@app/pages/directoryManager/dialogs/editAUSISyncAgent.dialog';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import { TableCell } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

interface IAgentDetailTableData {
    checked: boolean;
    agentName: string;
    lastModified: Date;
    provisioningState: string;
    agentType: string;
    id: string;
}

enum TableHeadEnum {
    agentName = 'agentName',
    lastModified = 'lastModified',
    agentType = 'agentType',
    provisioningState = 'provisioningState'
}
const DIRSYNC_2_TYPE = 'DirSync 2.0';

const SyncPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.DirectorySync);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('AgentName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IAgentDetailTableData[]>([]);
    const [reset, setReset] = useState(0);
    const [isFetchReset, setIsFetchReset] = useState(false);
    const [isAUSIEnabled, setIsAUSIEnabled] = useState(false);

    const syncs = useSelector((state: any) => state.syncs.syncs);
    const syncCount = useSelector((state: any) => state.syncs.count);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const [pageLoading, setPageLoading] = useState(false);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: true
    };

    const checkIsAUSIEnabled = () => {
        featureService
        .checkFeature(Features.Ausi)
        .then((res) => {
            setIsAUSIEnabled(res.data);
        })
        .catch(() => {});
    };

    const getAUSISyncAgent = (dirsyncAgentId: string) => {
        setPageLoading(true);
        syncService.getAUSISyncAgent(dirsyncAgentId).then((result: AxiosResponse) => {
            setPageLoading(false);
            editAUSIAgent(result.data);
        }).catch(() => {
            setPageLoading(false);
            toast.error(i18next.t('GENERIC_ERROR'));
        });
    };

    const editRow = (e: any) => {
        const agentId = e.currentTarget.dataset.id;
        const clickedAgent = rows.find((row) => (row.id === agentId));

        if (clickedAgent?.agentType === DIRSYNC_2_TYPE) {
            getAUSISyncAgent(agentId);
        } else {
            basicService.redirect('/directory/sync/details/' + e.currentTarget.dataset.id);
        }
    };

    const getComplexAgentTypeCell = (row: IAgentDetailTableData) => {
        return <TableCell>{row.agentType === DIRSYNC_2_TYPE ? translation('DIRSYNC_AGENT_TYPE_AUSI') : translation('DIRSYNC_AGENT_TYPE_DIRSYNC')}</TableCell>;
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.agentName,
            label: 'AGENT_NAME',
            type: 'link',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.agentName,
            onClick: editRow
        },
        {
            id: TableHeadEnum.agentType,
            label: 'DIRSYNC_AGENT_TYPE',
            type: 'getComplexCell',
            getComplexCell: getComplexAgentTypeCell
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'AGENT_LAST_CHECKIN',
            type: 'dateTime'
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'AGENT_STATE',
            type: 'status',
            filterType: FilterEnum.provisioningState,
            suggestionsForFilter: provisionStateSelectData
        }
    ];

    const actions: IFabAction[] = [
        {
            name: 'NEW_DIRSYNC_AGENT',
            method: () => addNewAgent(),
            iconElement: <i className='icon icon-directory-sync' />
        }
    ];

    if (isAUSIEnabled) {
        actions.push({
            name: 'NEW_AUSI_AGENT',
            method: () => addNewAUSIagent(),
            iconElement: <CloudUploadIcon />
        });
    }

    const menu: IFabMenu = {
        name: 'NEW_AGENT',
        actions: actions,
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        checkIsAUSIEnabled();
    }, []);

    useEffect(() => {
        if (!loadingData && syncs) {
            let newRows = syncs.map((sync: IAgentDetail) => {
                const data: IAgentDetailTableData = {
                    checked: false,
                    agentName: sync.AgentName,
                    lastModified: sync.LastModified,
                    provisioningState: sync.ProvisioningState,
                    agentType: sync.AgentType,
                    id: sync.Id
                };
                return data;
            });

            if (syncs.length === 0) {
                setRows([]);
            } else if (isFetchReset) {
                setIsFetchReset(false);
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, syncs]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (loadingData) {
            return;
        }
        setIsFetchReset(isReset);
        dispatch(handleGetSyncData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, organizationFilter, isReset, activeFilters));
    };

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const addNewAgent = () => {
        basicService.redirect('/directory/sync/onboarding');
    };

    const onAUSIAgentClose = () => {
        setReset(reset + 1);
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
    };

    const addNewAUSIagent = () => {
        setModalData({
            headerTitle: translation('ADD_AUSI_AGENT_TITLE'),
            modalSwitcher: modalSwitchState,
            form: <AddAUSIAgentDialog onCompleteCallback={onAUSIAgentClose} />
        });
        dispatch(getModalSwitch(true));
    };

    const editAUSIAgent = (dirSyncAUSIAgent: any) => {
        setModalData({
            headerTitle: translation('EDIT_AUSI_AGENT_TITLE'),
            modalSwitcher: modalSwitchState,
            form: (
                <EditAUSIAgentDialog agent={dirSyncAUSIAgent} onCompleteCallback={onAUSIAgentClose} />
            )
        });
        dispatch(getModalSwitch(true));
    };

    const emptyDirSyncList = () => {
        return !loadingData && rows.length === 0;
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        deleteSyncAgentAreYouSure(ids);
    }, [rows]);

    const deleteSyncAgentAreYouSure = (ids: any) => {
        confirm(translation('DELETE_AGENT_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_AGENT_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteSyncAgents(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteSyncAgents = (ids: string[]) => {
        if (ids.length <= 0) {
            return;
        }

        const {ds2, legacy} = ids.reduce((state, current) => {
            const agent = rows.find(row => row.id === current);
            if (agent?.agentType === DIRSYNC_2_TYPE) {
                state.ds2.push(current);
            } else {
                state.legacy.push(current);
            }
            return state;
        }, {ds2: [], legacy: []} as {ds2: string[], legacy: string[]});

        let error = false;
        let callCount = 0;
        let expectedCallCount = 0;
        const onResponse = (success: boolean) => {
            return (axiosError: AxiosError<any>) => {
                callCount++;
                if (!success) error = true;
                if (callCount === expectedCallCount) {
                    if (error) {
                        genericErrorToast(axiosError);
                        handlePageRefreshFromDialog(true);
                    }
                    else {
                        handleDeleteSuccess();
                    }
                }
            };
        };

        if (legacy.length > 0) {
            expectedCallCount++;
            syncService.deleteSyncAgent({ AgentProfiles: legacy }, onResponse(false), onResponse(true));
        }
        if (ds2.length > 0) {
            expectedCallCount++;
            syncService.deleteAUSISyncAgents(ds2, onResponse(true), onResponse(false));
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteSyncAgentAreYouSure(ids);
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {!hasOrgPermission([ManageSystem, ManageAccount]) ? (
                <div className='table--empty'>{translation('NO_ORG_SYNC_PERMISSIONS')}</div>
            ) : (
                <>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={syncCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={hasOrgPermission([ManageAccount])}
                        emptyListHelper={translation('EMPTY_AGENT_LIST')}
                        loadingData={loadingData}
                        actionMenuItems={actionMenuItems}
                        editRow={editRow}
                        deleteRow={deleteRow}
                        resetLocal={reset}
                    />

                    {pageLoading && <PageLoader />}

                    {!loadingData && (
                        <FabMenu
                            menu={menu}
                            hideTooltip={!emptyDirSyncList()}
                            tooltipTitle={translation('SYNCAGENTS_NO_AGENTS_TOOL_TIP_TITLE')}
                            tooltipDescription={translation('SYNCAGENTS_NO_AGENTS_TOOL_TIP_DESC')}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default SyncPage;
